/// <amd-module name="Wit/HtmlEditor/Utils/Mention" />
import { IAutocompleteProvider } from "Mention/Autocomplete/Contracts";
import { getAutocompleteProviders } from "Mention/AutocompleteResolver/AutocompleteContributionResolverService";
import { IObservableArrayEventArgs, IReadonlyObservableArray } from "VSS/Core/Observable";
import { isExternalUrl } from "VSS/Features/Markdown/Utils/UrlHelper";
import { IVssPageContext } from "VSS/Platform/Context";

const MENTION_BEGIN_TAG = "MentionBegin";
const MENTION_END_TAG = "MentionEnd";
const EXTRACT_HTML_REGEX = new RegExp(`<!--${MENTION_BEGIN_TAG}-->.+?<!--(<a[^>]+>.+?<\/a>)--><!--${MENTION_END_TAG}-->`, "gi");

export async function loadMentionProviders(pageContext: IVssPageContext): Promise<readonly IAutocompleteProvider[]> {
    return new Promise((resolve, reject) => {
        let done = false;
        const mentionProvidersObservable: IReadonlyObservableArray<IAutocompleteProvider> = getAutocompleteProviders(pageContext);
        const mentionProviders: IAutocompleteProvider[] = mentionProvidersObservable.value;
        const resolvedShortcuts = new Set<string>(mentionProviders.map(mp => mp.shortcutCharacter));

        const tryFinish = () => {
            if (done) {
                return;
            }

            if (resolvedShortcuts.has("#") && resolvedShortcuts.has("@") && resolvedShortcuts.has("!")) {
                mentionProvidersObservable.unsubscribe(onMentionProvidersUpdated);
                resolve(mentionProviders);
                done = true;
            }
        }

        const onMentionProvidersUpdated = (value: IObservableArrayEventArgs<IAutocompleteProvider>) => {
            if (done) {
                return;
            }

            value.addedItems?.forEach(mentionProvider => {
                mentionProviders.push(mentionProvider);
                resolvedShortcuts.add(mentionProvider.shortcutCharacter);
                tryFinish();
            });
        };

        tryFinish();

        if (done) {
            return;
        }

        mentionProvidersObservable.subscribe(onMentionProvidersUpdated);
        
        // Time-box this promise so that we can still show other previews if some providers don't load
        setTimeout(() => {
            if (done) {
                return;
            }

            mentionProvidersObservable.unsubscribe(onMentionProvidersUpdated);
            resolve(mentionProviders);
        }, 3000);
    });
}

export async function convertRawMentionsToRichPreviews(content: HTMLElement, pageContext: IVssPageContext): Promise<void> {
    tagMentionLinksForEasyRevert(content);

    // Wait for each mention provider to process mentions of the type it handles
    const mentionProviders = await loadMentionProviders(pageContext);
    await Promise.all(mentionProviders.map(mentionProvider => mentionProvider.transformHtml?.(content) ?? Promise.resolve()));
}

/**
 * To allow for easy unrendering of mentions, we create the rendered mention in this format:
 *
 *     <!--MentionBegin-->
 *     <RenderedMention />
 *     <!--<UnrenderedMention />-->
 *     <!--MentionEnd-->
 *
 * This way we can easily find the mentions in the rendered text and extract the unrendered version of it for un-rendering.
 * @param contentElement
 */
function tagMentionLinksForEasyRevert(contentElement: HTMLElement) {
    contentElement.querySelectorAll("a[data-vss-mention]").forEach(a => {
        a.parentElement?.insertBefore(document.createComment(MENTION_BEGIN_TAG), a);
        a.parentElement?.insertBefore(document.createComment(MENTION_END_TAG), a.nextSibling);
        a.parentElement?.insertBefore(document.createComment(a.outerHTML), a.nextSibling); // a.nextSibling is the END comment
    });
}

export function revertMentions(content: string): string {
    return content.replace(EXTRACT_HTML_REGEX, "$1");
}

/**
 * Goes through provided $html and makes sure that all external
 * links have target="_blank" and rel="noopener".
 * @param html Html to sanitize.
 */
export function sanitizeExternalLinks(element: HTMLElement) {
    for (const anchor of Array.from(element.querySelectorAll("a"))) {
        const href = anchor.getAttribute("href");
        if (href && isExternalUrl(href)) {
            anchor.setAttribute("target", "_blank");
            anchor.setAttribute("rel", "noopener noreferrer");
        }
    }
}
